import React, { useState, useEffect, useRef, useCallback } from "react"
import { Helmet } from "react-helmet"
import { ScrollContext } from "../context"
import { CATALOG } from "../data/catalog"
import { minmax } from "../utils/minmax"
import { useSpring } from "react-spring"
import get from "lodash/get"
import debounce from "lodash/debounce"
import PositionerMobile from "../components/PositionerMobile"
import { animated } from "react-spring"
import metaTags from './commonMeta'
import { useStaticQuery, graphql } from "gatsby"

const Hammer = typeof window !== "undefined" ? require("hammerjs") : null

const mc = typeof document !== "undefined" ? new Hammer(document) : undefined
mc && mc.get("pan").set({ direction: Hammer.DIRECTION_ALL })

const computeBoundaries = (y, after) => {
  const pages = Object.values(CATALOG)
  const hasAbovePage = pages.some(page => page.y === y - 1)
  const hasBelowPage = pages.some(page => page.y === y + 1)
  const isYMoving = y !== Math.floor(y)
  const next = {
    minY: Math.max(
      0,
      !isYMoving && hasAbovePage ? Math.floor(y) - 1 : Math.floor(y)
    ),
    maxY: hasBelowPage ? Math.ceil(y) + 1 : Math.ceil(y),
  }
  if (after) {
    after(next)
  } else {
    return next
  }
}

const computeBoundariesDebounced = debounce(computeBoundaries, 1000, {
  leading: true,
})

const TemplateWrapper = ({ isMobile, children, location, ...props }) => {
  const currentItem = CATALOG[location.pathname]
  const [y, setY] = useState(get(currentItem, "y", 0))
  const [p, setP] = useState(get(currentItem, "y", 0))
  const domRef = useRef(null)
  const innerRef = useRef(null)
  const [boundaries, setBoundaries] = useState(computeBoundaries(y))
  const isImmediate = useRef(true)
  const currentTouch = useRef(null)
  const [showFader, setShowFader] = useState(false)

  const { site } = useStaticQuery(
    graphql`
      query {
        site {
          siteMetadata {
            siteUrl,
            siteImage,
          }
        }
      }
    `)
  const siteUrl = get(site, 'siteMetadata.siteUrl')
  const siteImage = get(site, 'siteMetadata.siteImage')

  useEffect(() => {
    const domNode = domRef.current
    const onTouchStart = e => {
      const canGoUp = innerRef.current !== null && innerRef.current.scrollTop < 10
      const canGoDown = innerRef.current !== null && Math.ceil(innerRef.current.scrollTop + innerRef.current.offsetHeight) + 10 >= innerRef.current.scrollHeight
      currentTouch.current = [e.touches[0].pageY, e.touches[0].pageY, canGoUp, canGoDown]
    }
    const onTouchMove = e => {
      const [startPoint, reference, canGoUp, canGoDown] = currentTouch.current
      const delta = - (e.touches[0].pageY - reference)
      const overallDelta = - (e.touches[0].pageY - startPoint)
      if ((overallDelta < 0 && canGoUp) || (overallDelta > 0 && canGoDown)) {
        setY(y => {
          return minmax(
            y + delta / window.innerHeight,
            boundaries.minY,
            boundaries.maxY
          )
        })
        currentTouch.current = [startPoint, e.touches[0].pageY, canGoUp, canGoDown]
        e.preventDefault()
      }
    }
    const onTouchEnd = () => {
      setY(y => minmax(Math.round(y), boundaries.minY, boundaries.maxY))
    }
    const onScroll = e => {
      if (currentTouch.current) {
        e.preventDefault()
      }
    }

    if (domNode) {
      domNode.addEventListener("touchstart", onTouchStart)
      domNode.addEventListener("touchmove", onTouchMove, { passive: false, cancelable: true })
      domNode.addEventListener("touchend", onTouchEnd)
      domNode.addEventListener("scroll", onScroll, { passive: false })

      return () => {
        domNode.removeEventListener("touchstart", onTouchStart)
        domNode.removeEventListener("touchmove", onTouchMove, { passive: false })
        domNode.removeEventListener("touchend", onTouchEnd)
        domNode.removeEventListener("scroll", onScroll, { passive: false })
      }
    }
  }, [boundaries])


  useEffect(() => {
    computeBoundariesDebounced(y, setBoundaries)
  }, [y])


  const spring = useSpring({
    to: { y },
    clamp: true,
    onChange: ({ value : { y } }) => {
      if (Math.round(y) !== p[1]) {
        setP(Math.round(y))
      }
    },
    onRest: () => {
      isImmediate.current = false
    },
    immediate: isImmediate.current,
  })

  const opacitySpring = useSpring({
    from: { opacity: showFader ? 1 : 0 },
    to: { opacity: showFader ? 0 : 1 },
    config: {
      duration: 1000,
    },
  })

  const navigate = useCallback(
    pathname => {
      const currentItem = CATALOG[pathname]
      const nextY = get(currentItem, "y", 0)
      if (p !== nextY) {
        isImmediate.current = true
        setShowFader(true)
        setY(nextY)
        setP(nextY)
        setBoundaries(computeBoundaries(nextY))
        setTimeout(() => setShowFader(false), 1000)
      }
    },
    [p]
  )

  return (
    <div
      className={`mobile-root ${isMobile ? "isMobile" : "isDesktop"}`}
      ref={domRef}
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        bottom: 0,
        right: 0,
        margin: 8,
        borderColor: "#005BBB",
        borderStyle: "solid",
        borderWidth: 2,
        borderRadius: 20,
      }}
    >
      <Helmet
        title="L'e-mobility per il business"
        meta={metaTags(siteUrl)}
      />
      <ScrollContext.Provider
        value={{
          ...spring,
          p,
          h: get(domRef.current, "offsetHeight", 0),
        }}
      >
        <PositionerMobile
          location={location}
          navigate={navigate}
          innerRef={innerRef}
        />
        {showFader && (
          <animated.div
            className="XXXXXXXXXXXX"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              ...opacitySpring,
              backgroundColor: "black",
              zIndex: 10000,
            }}
          />
        )}
      </ScrollContext.Provider>
    </div>
  )
}

export default TemplateWrapper
